import React, { createContext, useContext, useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import { useLocation } from "react-router-dom"; // Ensure you're using React Router
import useResponsive from "./useResponsive";

interface DrawerContextProps {
  openDrawer: (component: React.ReactNode) => void;
  closeDrawer: () => void;
}

const DrawerContext = createContext<DrawerContextProps | undefined>(undefined);

export const DrawerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<React.ReactNode>(null);
  const location = useLocation(); // React Router hook for detecting route changes
  const isSmall = useResponsive("down", "sm", "xl");

  const openDrawer = (component: React.ReactNode) => {
    setContent(component);
    setIsOpen(true);
  };

  const closeDrawer = () => {
    setIsOpen(false);
    setContent(null);
  };

  // Close the drawer when the route changes
  useEffect(() => {
    if (isOpen) {
      closeDrawer();
    }
  }, [location]);

  return (
    <DrawerContext.Provider
      value={{
        openDrawer,
        closeDrawer,
      }}
    >
      {children}
      <Drawer open={isOpen} onClose={closeDrawer} anchor="right">
        <Box
          sx={{ width: isSmall ? "100%" : 500, mx: isSmall ? "auto" : "0px" }}
          role="presentation"
        >
          {content}
        </Box>
      </Drawer>
    </DrawerContext.Provider>
  );
};

export const useDrawer = () => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error("useDrawer must be used within a DrawerProvider");
  }
  return context;
};
